<!-- ZayaAI UI-4 (refinement): image detail pop-up.
     Constrained modal-card (not full-width). Left column: enlarged macro preview + slide
     label (both zoomable) + original filename + the image Actions. Right column: Case
     Information first, then Image Information (description / tags / properties / attached
     files). Image metadata is intentionally NOT duplicated here (it lives on the Metadata
     action button).

     The image Actions (rename / calibration / magnification / metadata / delete / add-to-
     image-group, with their modals) are reused from ImageDetails by excluding every field
     row so only its Actions row renders — no logic duplicated, no refactor of the shared
     ImageDetails (used by 4 other consumers). The Image-Information fields are composed from
     the same sub-components ImageDetails uses. See specs/2026-06-01-zayaai-ui-project-page/. -->
<template>
<b-modal :active="active" has-modal-card scroll="keep" @update:active="v => $emit('update:active', v)">
  <div class="modal-card idm-card" v-if="image">
    <header class="modal-card-head idm-head">
      <span class="idm-title"><image-name :image="image" showBothNames /></span>
      <div class="idm-head-tools">
        <router-link :to="viewerRoute" class="button is-link is-small">{{ $t('view-image') }}</router-link>
        <button class="delete" :aria-label="$t('button-close')" @click="close"></button>
      </div>
    </header>

    <section class="modal-card-body idm-body">
      <div class="idm-grid">
        <aside class="idm-side">
          <div class="idm-media">
            <a v-if="hasMacro" class="idm-big" @click="openZoom('macro')" :title="$t('slide-preview')">
              <image-thumbnail :image="image" :macro="true" :size="640" :key="`${image.id}-macro-640`" :extra-parameters="tokenParam"/>
              <span class="zoom-badge"><i class="fas fa-expand"></i></span>
            </a>
            <em v-else class="idm-muted">{{ $t('slide-preview-not-available') }}</em>

            <div v-if="hasMacro && hasLabel" class="idm-label">
              <span class="idm-cap">{{ $t('slide-label') }}</span>
              <a class="idm-label-body" @click="openZoom('label')" :title="$t('slide-label')">
                <image-thumbnail :image="image" :label="true" :size="320" :key="`${image.id}-label-320`" :extra-parameters="tokenParam" @error="hasLabel = false"/>
                <span class="zoom-badge corner"><i class="fas fa-expand"></i></span>
              </a>
            </div>

            <div v-if="!blindMode || canManageProject" class="idm-fn">
              <span class="idm-cap">{{ $t('originalFilename') }}</span>
              <b>{{ image.originalFilename }}</b>
            </div>

            <!-- key technical metadata (format / dimensions / resolution / magnification),
                 the same fields/properties ImageDetails renders, surfaced here next to the
                 filename per request. Not patient-identifying, so shown regardless of blind mode. -->
            <dl class="idm-meta">
              <div class="idm-meta-row">
                <dt>{{ $t('format') }}</dt>
                <dd>{{ image.contentType || $t('unknown') }}</dd>
              </div>
              <div class="idm-meta-row">
                <dt>{{ $t('image-width') }}</dt>
                <dd>{{ image.width }} {{ $t('pixels') }}</dd>
              </div>
              <div class="idm-meta-row">
                <dt>{{ $t('image-height') }}</dt>
                <dd>{{ image.height }} {{ $t('pixels') }}</dd>
              </div>
              <div class="idm-meta-row">
                <dt>{{ $t('x-y-resolution') }}</dt>
                <dd>
                  <template v-if="image.physicalSizeX">{{ image.physicalSizeX.toFixed(3) }} {{ $t('um-per-pixel') }}</template>
                  <template v-else>{{ $t('unknown') }}</template>
                </dd>
              </div>
              <div class="idm-meta-row">
                <dt>{{ $t('magnification') }}</dt>
                <dd>
                  <template v-if="image.magnification">{{ image.magnification }}</template>
                  <template v-else>{{ $t('unknown') }}</template>
                </dd>
              </div>
            </dl>
          </div>

          <div class="idm-actions">
            <image-details
              :image="image"
              :excludedProperties="actionsOnlyExcluded"
              editable
              @delete="onDelete"
              @refresh="$emit('refresh')"
              @setResolution="$emit('refresh')"
              @setMagnification="$emit('refresh')"
            />
          </div>
        </aside>

        <div class="idm-main">
          <section class="idm-section">
            <h3 class="idm-section-title">{{ $t('case-information-title') }}</h3>
            <case-information-panel :image="image" />
          </section>

          <section class="idm-section">
            <h3 class="idm-section-title">{{ $t('information') }}</h3>
            <div class="idm-fields">
              <div class="idm-field">
                <span class="idm-field-l">{{ $t('description') }}</span>
                <div class="idm-field-v"><ims-description :object="image" :canEdit="canEdit" /></div>
              </div>
              <div class="idm-field">
                <span class="idm-field-l">{{ $t('tags') }}</span>
                <div class="idm-field-v"><ims-tags :object="image" :canEdit="canEdit" /></div>
              </div>
              <div class="idm-field">
                <span class="idm-field-l">{{ $t('properties') }}</span>
                <div class="idm-field-v"><ims-properties :object="image" :canEdit="canEdit" /></div>
              </div>
              <div class="idm-field">
                <span class="idm-field-l">{{ $t('attached-files') }}</span>
                <div class="idm-field-v"><attached-files :object="image" :canEdit="canEdit" /></div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </section>
  </div>

  <div v-if="zoom" class="zoomscrim" @click.self="zoom = null">
    <div class="zoombox">
      <div class="zb-head">{{ zoom === 'label' ? $t('slide-label') : $t('slide-preview') }}</div>
      <div class="zb-body" :class="{label: zoom === 'label'}">
        <img :src="zoomSrc" alt="">
      </div>
      <div class="zb-foot">
        <router-link v-if="zoom === 'macro'" :to="viewerRoute" class="button is-link">{{ $t('view-image') }}</router-link>
        <button class="button" @click="zoom = null">{{ $t('button-close') }}</button>
      </div>
    </div>
  </div>
</b-modal>
</template>

<script>
import {get} from '@/utils/store-helpers';
import {appendShortTermToken} from '@/utils/token-utils.js';

import AttachedFiles from '@/components/attached-file/AttachedFiles';
import CaseInformationPanel from '@/components/image/CaseInformationPanel';
import ImsDescription from '@/components/description/ImsDescription';
import ImsProperties from '@/components/property/ImsProperties';
import ImsTags from '@/components/tag/ImsTags';
import ImageDetails from '@/components/image/ImageDetails';
import ImageName from '@/components/image/ImageName';
import ImageThumbnail from '@/components/image/ImageThumbnail';

export default {
  name: 'image-detail-modal',
  components: {
    AttachedFiles,
    CaseInformationPanel,
    ImsDescription,
    ImsProperties,
    ImsTags,
    ImageDetails,
    ImageName,
    ImageThumbnail
  },
  props: {
    active: {type: Boolean, default: false},
    image: {type: Object, default: null}
  },
  data() {
    return {
      hasLabel: true,
      zoom: null, // 'macro' | 'label' | null
      // Exclude every ImageDetails field row so only its Actions row renders (its action
      // logic + modals are reused as-is). 'metadata' is kept so the Metadata button shows.
      actionsOnlyExcluded: [
        'overview', 'id', 'status', 'numberOfAnnotations', 'numberOfReviewedAnnotations',
        'description', 'tags', 'properties', 'attached-files', 'slide-preview',
        'original-filename', 'format', 'vendor', 'width', 'physicalSizeX', 'height',
        'physicalSizeY', 'depth', 'physicalSizeZ', 'channels', 'size', 'magnification'
      ]
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
    project: get('currentProject/project'),
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    canEdit() {
      return this.image ? this.$store.getters['currentProject/canEditImage'](this.image) : false;
    },
    blindMode() {
      return ((this.project || {}).blindMode) || false;
    },
    isBlindModeAndContributor() {
      return this.blindMode && !this.canManageProject;
    },
    hasMacro() {
      return !!(this.image && this.image.macroURL) && !this.isBlindModeAndContributor;
    },
    tokenParam() {
      return {authorization: 'Bearer ' + this.shortTermToken};
    },
    viewerRoute() {
      return this.image ? `/project/${this.image.project}/image/${this.image.id}` : '';
    },
    zoomSrc() {
      if (!this.image || !this.zoom) {
        return '';
      }
      return appendShortTermToken(
        this.image.associatedImageURL(this.zoom, 1024, 'jpg'), this.shortTermToken
      );
    }
  },
  watch: {
    image() {
      this.hasLabel = true;
      this.zoom = null;
    }
  },
  methods: {
    close() {
      this.$emit('update:active', false);
    },
    onDelete() {
      this.$emit('refresh');
      this.close();
    },
    openZoom(kind) {
      this.zoom = kind;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/colors.scss';

.idm-card {
  width: 940px;
  max-width: calc(100vw - 40px);
  border-radius: 12px;
  overflow: hidden;
}

.idm-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.3rem;
  background: #fff;
  border-bottom: 1px solid $line-1;
}

.idm-title {
  font-weight: 700;
  font-size: 1.05rem;
  color: $ink-1;
  min-width: 0;
}

.idm-head-tools {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  flex: 0 0 auto;
}

.idm-body { padding: 0; }

.idm-grid {
  display: grid;
  grid-template-columns: 340px 1fr;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.idm-side {
  padding: 1.2rem 1.25rem;
  border-right: 1px solid $line-1;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;

  @media screen and (max-width: 768px) {
    border-right: 0;
    border-bottom: 1px solid $line-1;
  }
}

/* Media (preview + label + filename) stacks compactly at the top of the column.
   Previously this stretched (flex:1 1 auto) and vertically-centred its content, which
   pushed the Actions to the bottom and left a large gap in between. Top-aligned + no
   stretch removes that whitespace and reads as a tidy panel. */
.idm-media {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  flex: 0 0 auto;
}

.idm-big {
  position: relative;
  display: block;
  width: 100%;
  min-height: 150px;
  border: 1px solid $line-1;
  border-radius: 10px;
  overflow: hidden;
  background: #f4f5f7;
  cursor: zoom-in;
  display: flex;
  align-items: center;
  justify-content: center;

  ::v-deep .image-thumbnail { width: 100%; display: block; }
  &:hover .zoom-badge { background: $primary; }
}

.zoom-badge {
  position: absolute;
  right: 8px;
  bottom: 8px;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: rgba(38, 42, 49, 0.82);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.idm-label {
  border: 1px solid $line-1;
  border-radius: 10px;
  padding: 0.8rem 0.85rem;
  background: #fff;
}

.idm-cap {
  display: block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #8a91a1;
  margin-bottom: 0.5rem;
}

.idm-label-body {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-in;
  min-height: 110px;

  ::v-deep .image-thumbnail { max-width: 100%; max-height: 180px; display: block; }
  &:hover .zoom-badge { background: $primary; }
}

.idm-fn {
  font-size: 0.85rem;
  color: $ink-1;

  b { display: block; font-weight: 600; word-break: break-all; margin-top: 0.15rem; }
}

/* technical metadata — clean hairline key/value rows (design: a tidy spec table) */
.idm-meta {
  margin: 0;
  border: 1px solid $line-1;
  border-radius: 10px;
  overflow: hidden;
}
.idm-meta-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 0.75rem;
  align-items: baseline;
  padding: 0.5rem 0.85rem;
  font-size: 0.83rem;

  & + .idm-meta-row { border-top: 1px solid $line-1; }

  dt { font-weight: 600; color: $ink-1; }
  dd { margin: 0; color: $ink-3; word-break: break-word; }
}

.idm-muted { color: #8a91a1; font-style: italic; }

/* Actions block (reused ImageDetails actions-only) — make it read as a clean panel */
.idm-actions {
  border-top: 1px solid $line-1;
  padding-top: 0.5rem;

  ::v-deep .table,
  ::v-deep .table td {
    border: 0;
    background: transparent;
    padding-left: 0;
    padding-right: 0;
  }
  ::v-deep .prop-label {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #8a91a1;
    padding-bottom: 0.4rem;
  }
  ::v-deep .prop-content { display: block; }
  ::v-deep tr { display: block; }
}

.idm-main {
  padding: 1.1rem 1.3rem 1.3rem;
  min-width: 0;
}

.idm-section { margin-bottom: 1.4rem; }
.idm-section:last-child { margin-bottom: 0; }

.idm-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: $ink-1;
  margin-bottom: 0.8rem;
  padding-bottom: 0.45rem;
  border-bottom: 1px solid $line-1;
}

.idm-fields {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.idm-field {
  display: grid;
  grid-template-columns: 130px 1fr;
  align-items: start;
  gap: 0.5rem 1rem;

  @media screen and (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
}

.idm-field-l {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #8a91a1;
  padding-top: 0.2rem;
}

.idm-field-v { min-width: 0; }

/* Zoom lightbox (design: .zoomscrim / .zoombox) */
.zoomscrim {
  position: fixed;
  inset: 0;
  background: rgba(12, 14, 18, 0.78);
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 36px;
}

.zoombox {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.4);
  max-width: 680px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  max-height: 90vh;
}

.zb-head {
  font-size: 0.85rem;
  font-weight: 600;
  color: $ink-3;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.zb-body {
  border: 1px solid $line-1;
  border-radius: 10px;
  overflow: auto;
  background: #f4f5f7;
  text-align: center;

  img { max-width: 100%; display: inline-block; }
  &.label { padding: 24px; background: #fff; }
}

.zb-foot {
  display: flex;
  justify-content: center;
  gap: 0.8rem;
}
</style>
